import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Shield, Users, Save, Search, ChevronDown, ChevronUp,
  Eye, PenLine, FilePlus, Trash2, Info, ShieldCheck
} from 'lucide-react';

export const MENU_ITEMS = [
  {
    group: 'Core',
    items: [
      { key: 'projects',         label: 'Projects',              description: 'View, add, edit, delete projects, items, MO references, chain setup' },
      { key: 'main_po',          label: 'Manufacturing Order',               description: 'Create Manufacturing Order, submit for approval; approved Manufacturing Order auto-creates the Project (Read=View/Print, Write=Add/Submit own, Edit=Edit draft, Delete=Delete draft)' },
      { key: 'quotations',       label: 'Quotations',            description: 'Price quotations against a Manufacturing Order (Read=View/Print, Write=New/Revise/Mark Sent·Accepted·Rejected, Edit=Edit draft, Delete=Delete draft)' },
      { key: 'finish',           label: 'Finish Sheets',         description: 'Paint/Fabric/Metal finish sheets per Manufacturing Order (Read=View/Print, Write=Fill/Save/Submit, Edit=Delete images)' },
      { key: 'cash_book',        label: 'Cash Book',             description: 'Advances & expenses (Read=View own/Print, Write=Request advance/Add expense/Attach bills, Delete=Delete own pending). Approve/Reject = admin via Approvals' },
      { key: 'payroll',          label: 'Payroll',               description: 'Employees & time entry (Read=View/Print, Write=Add/Edit/Import employee + Enter time, Delete=Delete). Rates, amounts & reports are ADMIN ONLY' },
      { key: 'quality_report',   label: 'Quality Reports',       description: 'Inspection reports (Read=View/Print, Write=Create/Upload/Submit + add sections when permitted, Edit=Assign/configure, Delete=Delete draft). Approve/Reject = admin via Approvals' },
      { key: 'cft',              label: 'CFT Sheets',            description: 'Solid Wood Issue Sheets — demand/issue/return CFT with fixed 30% offcut (Read=View/Print, Write=Add/Edit/Submit, Delete=Delete draft). Reopen = admin' },
      { key: 'tasks',            label: 'Tasks',                 description: 'View all tasks, assign, edit, transfer, delete tasks' },
      { key: 'workers',          label: 'Workers',               description: 'View, add, edit, delete workers and department assignments' },
      { key: 'departments',      label: 'Departments',           description: 'View, add, edit, delete departments and gallery files' },
      { key: 'clients',          label: 'Clients',               description: 'View and manage client list' },
    ]
  },
  {
    group: 'Reports & Tracking',
    items: [
      { key: 'reports',          label: 'Reports',               description: 'View worker, department, transfer, material reports' },
      { key: 'client_po_report',  label: 'PO Report',             description: 'View Client PO vs Sale vs Payment summary report' },
      { key: 'product_tracking', label: 'Stage Tracker',         description: 'View product tracking across production stages' },
      { key: 'time_report',      label: 'Time Report',           description: 'View worker time logs and session reports' },
      { key: 'daily_progress',   label: 'Daily Progress',        description: 'View and submit daily production progress entries' },
      { key: 'materials',        label: 'Materials',             description: 'View, add, edit, delete material consumption logs' },
    ]
  },
  {
    group: 'Operations',
    items: [
      { key: 'packing',          label: 'Packing',               description: 'View, create, edit packing boxes; manage packing parts' },
      { key: 'barcodes',         label: 'Barcodes',              description: 'Scan and print barcodes for project items' },
      { key: 'dispatch',         label: 'Dispatch (Challans)',    description: 'Create and manage delivery challans (worker side)' },
      { key: 'outsource',        label: 'Outsource Tracker',     description: 'View, create, edit, delete outsource jobs sent to vendors' },
    ]
  },
  {
    group: 'Sales & Finance',
    items: [
      { key: 'sales',            label: 'Sales / Invoices',      description: 'View and manage sales invoices' },
      { key: 'sale_challans',    label: 'Sale Challans',         description: 'View, create, edit, delete sale challans; upload PDFs' },
      { key: 'client_pos',       label: 'Client POs',            description: 'View and process client purchase orders' },
    ]
  },
  {
    group: 'Support & Admin',
    items: [
      { key: 'client_support',   label: 'Client Support',        description: 'View and respond to client support tickets' },
      { key: 'queries',          label: 'Queries',               description: 'View and respond to worker queries' },
      { key: 'terms',            label: 'Terms & Conditions',    description: 'Saved terms for quotations (Read=View, Write=New, Edit=Change / make default, Delete=Delete)' },
      { key: 'settings',         label: 'Settings',              description: 'View and update application settings' },
    ]
  },
];

export const ALL_MENU_KEYS = MENU_ITEMS.flatMap(g => g.items);

const LEVELS = [
  { key: 'can_read',   label: 'Read',   icon: Eye,      color: 'blue',  desc: 'View only' },
  { key: 'can_write',  label: 'Write',  icon: FilePlus, color: 'green', desc: 'View + Add' },
  { key: 'can_edit',   label: 'Edit',   icon: PenLine,  color: 'amber', desc: 'View + Add + Edit' },
  { key: 'can_delete', label: 'Delete', icon: Trash2,   color: 'red',   desc: 'Full access' },
];

const COLOR_CLASSES = {
  blue:  { bg: 'bg-blue-100',  text: 'text-blue-700',  border: 'border-blue-300',  active: 'bg-blue-500 text-white border-blue-500' },
  green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', active: 'bg-green-500 text-white border-green-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', active: 'bg-amber-500 text-white border-amber-500' },
  red:   { bg: 'bg-red-100',   text: 'text-red-700',   border: 'border-red-300',   active: 'bg-red-500 text-white border-red-500' },
};

function PermissionBadge({ level, active, onClick }) {
  const Icon = level.icon;
  const c = COLOR_CLASSES[level.color];
  return (
    <button onClick={onClick} title={level.desc}
      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-all select-none ${
        active ? c.active : `${c.bg} ${c.text} ${c.border} hover:opacity-80`
      }`}
    >
      <Icon className="w-3 h-3" />
      <span className="hidden sm:inline">{level.label}</span>
    </button>
  );
}

function autoFill(current, levelKey, newVal) {
  const p = { ...current };
  p[levelKey] = newVal ? 1 : 0;
  if (levelKey === 'can_delete' && newVal) {
    p.can_read = 1; p.can_write = 1; p.can_edit = 1; p.can_delete = 1;
  } else if (levelKey === 'can_edit' && newVal) {
    p.can_read = 1; p.can_write = 1; p.can_edit = 1;
  } else if (levelKey === 'can_write' && newVal) {
    p.can_read = 1; p.can_write = 1;
  } else if (levelKey === 'can_read' && !newVal) {
    p.can_read = 0; p.can_write = 0; p.can_edit = 0; p.can_delete = 0;
  } else if (!newVal) {
    if (levelKey === 'can_write') { p.can_edit = 0; p.can_delete = 0; }
    if (levelKey === 'can_edit')  { p.can_delete = 0; }
  }
  return p;
}

function WorkerCard({ worker, permMap, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const buildEmpty = () => {
    const p = {};
    ALL_MENU_KEYS.forEach(m => { p[m.key] = permMap[m.key] || { can_read: 0, can_write: 0, can_edit: 0, can_delete: 0 }; });
    return p;
  };
  const [localPerms, setLocalPerms] = useState(buildEmpty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocalPerms(buildEmpty()); }, [permMap]);

  const toggle = (menuKey, levelKey) => setLocalPerms(prev => ({
    ...prev, [menuKey]: autoFill(prev[menuKey] || {}, levelKey, !prev[menuKey]?.[levelKey])
  }));

  const grantAll  = () => { const p = {}; ALL_MENU_KEYS.forEach(m => { p[m.key] = { can_read:1, can_write:1, can_edit:1, can_delete:1 }; }); setLocalPerms(p); };
  const revokeAll = () => { const p = {}; ALL_MENU_KEYS.forEach(m => { p[m.key] = { can_read:0, can_write:0, can_edit:0, can_delete:0 }; }); setLocalPerms(p); };
  const readAll   = () => { const p = {}; ALL_MENU_KEYS.forEach(m => { p[m.key] = autoFill({}, 'can_read', true); }); setLocalPerms(p); };

  const grantGroup = (items, level) => setLocalPerms(prev => {
    const next = { ...prev };
    items.forEach(m => { next[m.key] = autoFill(next[m.key] || {}, level, true); });
    return next;
  });
  const revokeGroup = (items) => setLocalPerms(prev => {
    const next = { ...prev };
    items.forEach(m => { next[m.key] = { can_read:0, can_write:0, can_edit:0, can_delete:0 }; });
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(worker.id, localPerms); toast.success(`Permissions saved for ${worker.name}`); }
    catch { toast.error('Failed to save permissions'); }
    finally { setSaving(false); }
  };

  const activeCount = ALL_MENU_KEYS.filter(m => localPerms[m.key]?.can_read || localPerms[m.key]?.can_write || localPerms[m.key]?.can_edit || localPerms[m.key]?.can_delete).length;

  return (
    <div className={`bg-white rounded-xl border-2 transition-all ${expanded ? 'border-amber-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
      <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {worker.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{worker.name}</p>
          <p className="text-xs text-gray-500">@{worker.username}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${activeCount > 0 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
            {activeCount}/{ALL_MENU_KEYS.length} menus
          </span>
          {!worker.is_active && <span className="bg-red-100 text-red-600 text-xs font-medium px-2 py-0.5 rounded-full">Inactive</span>}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-4">
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={grantAll}  className="text-xs px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 font-medium">✓ Grant All (Full)</button>
            <button onClick={readAll}   className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 font-medium">👁 Read Only All</button>
            <button onClick={revokeAll} className="text-xs px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 font-medium">✗ Revoke All</button>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
            {LEVELS.map(l => (
              <span key={l.key} className="flex items-center gap-1">
                <l.icon className="w-3 h-3" />
                <strong>{l.label}:</strong> {l.desc}
              </span>
            ))}
          </div>

          {/* Groups */}
          <div className="space-y-5">
            {MENU_ITEMS.map(group => (
              <div key={group.group}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{group.group}</h3>
                  <div className="flex gap-1.5">
                    <button onClick={() => grantGroup(group.items, 'can_read')}   className="text-[10px] px-2 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium hover:bg-blue-100">Read</button>
                    <button onClick={() => grantGroup(group.items, 'can_delete')} className="text-[10px] px-2 py-1 bg-green-50 text-green-600 border border-green-200 rounded font-medium hover:bg-green-100">Full</button>
                    <button onClick={() => revokeGroup(group.items)}              className="text-[10px] px-2 py-1 bg-red-50 text-red-600 border border-red-200 rounded font-medium hover:bg-red-100">None</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {group.items.map(menu => {
                    const perm = localPerms[menu.key] || {};
                    const hasAny = perm.can_read || perm.can_write || perm.can_edit || perm.can_delete;
                    return (
                      <div key={menu.key}
                        className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl border transition-all ${hasAny ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800">{menu.label}</p>
                          <p className="text-xs text-gray-400 mt-0.5 leading-tight">{menu.description}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          {LEVELS.map(level => (
                            <PermissionBadge key={level.key} level={level} active={!!perm[level.key]} onClick={() => toggle(menu.key, level.key)} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 flex justify-end">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold text-sm hover:bg-amber-600 disabled:opacity-50 transition-all shadow-sm">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function UserRightsPage() {
  const [workers, setWorkers] = useState([]);
  const [permissionsMap, setPermissionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/user-rights');
      setWorkers(res.data.workers || []);
      setPermissionsMap(res.data.permissions || {});
    } catch { toast.error('Failed to load user rights'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (workerId, permissions) => {
    await api.put(`/user-rights/${workerId}`, { permissions });
    load();
  };

  const filtered = workers.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    w.username.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">User Rights</h1>
            <p className="text-sm text-gray-500">Manage menu access and permissions for each worker</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex gap-2 text-sm text-blue-800">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
          <div>
            <strong>Note:</strong> Dashboard, My Tasks, and Queries are always visible to all workers. All other menus require explicit permission below.
            <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-blue-600">
              <span><Eye className="w-3 h-3 inline mr-0.5" /><strong>Read</strong> = view only</span>
              <span><FilePlus className="w-3 h-3 inline mr-0.5" /><strong>Write</strong> = view + add</span>
              <span><PenLine className="w-3 h-3 inline mr-0.5" /><strong>Edit</strong> = view + add + edit</span>
              <span><Trash2 className="w-3 h-3 inline mr-0.5" /><strong>Delete</strong> = full access</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search workers by name or username..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-3" />
          Loading workers...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{search ? 'No workers match your search' : 'No workers found'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(worker => (
            <WorkerCard key={worker.id} worker={worker} permMap={permissionsMap[worker.id] || {}} onSave={handleSave} />
          ))}
        </div>
      )}
    </div>
  );
}
